// useAttestation — item 12 (Asana 1216373781992284), Owner Attestation, capture layer only.
//
// Talks to /api/framework/attestation/*: the question schema (with Q5/Q10's engagement-config +
// inventory-derived options already resolved server-side), the engagement config lists themselves,
// per-EUC answers, the coverage/gaps rollup, and the export/import round-trip. No scoring lives
// here — this hook is purely capture: ask, store, track coverage, export a pack, import responses.

import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../api';
import { useProject } from '../../../contexts/ProjectContext';

export interface AttestationOption { value: string; label: string; extra?: { label: string; placeholder?: string; type?: string } | null; }
export interface AttestationQuestion {
  id: string;
  prompt: string;
  kind: 'single' | 'multi';
  feeds: string;
  optional: boolean;
  parent: string | null;
  hasSubQuestion: string | null;
  extra: { label: string; placeholder?: string; type?: string } | null;
  options: AttestationOption[];
}

export interface AttestationAnswer {
  value: string | string[];
  extra?: string | null;
  actor?: { id: number | string; name: string | null; email: string | null; role?: string | null };
  at?: string;
}
export type EucAnswers = Record<string, AttestationAnswer>;          // questionId -> answer
export type AllAnswers = Record<string, EucAnswers>;                 // eucId -> questionId -> answer

export interface AttestationConfig { submission_list: string[]; system_list: string[]; }

export interface CoverageEucRow {
  euc_id: string; file: string; dept: string; owner: string | null;
  answered: number; total: number; missing: string[];
}
export interface CoverageOwnerRow { owner: string; euc_count: number; answered_total: number; possible_total: number; pct: number; }
export interface Coverage {
  total_eucs: number; total_questions: number; answered_total: number; possible_total: number; pct: number;
  by_euc: CoverageEucRow[]; by_owner: CoverageOwnerRow[];
}

export interface ImportResult {
  imported: { euc_id: string; questions: string[] }[];
  errors: { euc_id: string; question_id: string | null; error: string }[];
  rows_total: number; eucs_imported: number; error_count: number;
}

export interface UseAttestation {
  questions: AttestationQuestion[];
  required: string[];
  questionsLoaded: boolean;
  loadQuestions: () => Promise<void>;

  config: AttestationConfig;
  configLoaded: boolean;
  loadConfig: () => Promise<void>;
  saveConfig: (config: AttestationConfig) => Promise<{ ok: boolean; error?: string }>;

  answers: AllAnswers;
  answersLoaded: boolean;
  loadAnswers: () => Promise<void>;
  saveAnswers: (eucId: string, answers: Record<string, { value: string | string[]; extra?: string | null }>) => Promise<{ ok: boolean; saved?: EucAnswers; errors?: { question_id: string; error: string }[]; error?: string }>;

  coverage: Coverage | null;
  coverageLoading: boolean;
  loadCoverage: () => Promise<void>;

  exportPack: (owner: string) => Promise<{ ok: boolean; error?: string }>;
  importFile: (file: File) => Promise<{ ok: boolean; result?: ImportResult; error?: string }>;
  importRows: (rows: { euc_id: string; question_id: string; response: string; notes?: string }[]) => Promise<{ ok: boolean; result?: ImportResult; error?: string }>;
}

const EMPTY_CONFIG: AttestationConfig = { submission_list: [], system_list: [] };

export function useAttestation(): UseAttestation {
  // Bug fix 2026-08-08 (Asana 1217193001422681): every call below was hitting
  // /api/framework/attestation/* with no project_id, so the backend fell back to the legacy
  // tenant-wide snapshot instead of the current project's inventory — same root cause, same fix
  // pattern as useFrameworkData.ts's projectId derivation.
  const { currentProject } = useProject();
  const projectId = currentProject?.id != null && Number.isFinite(Number(currentProject.id))
    ? Number(currentProject.id)
    : null;

  const [questions, setQuestions] = useState<AttestationQuestion[]>([]);
  const [required, setRequired] = useState<string[]>([]);
  const [questionsLoaded, setQuestionsLoaded] = useState(false);

  const [config, setConfig] = useState<AttestationConfig>(EMPTY_CONFIG);
  const [configLoaded, setConfigLoaded] = useState(false);

  const [answers, setAnswers] = useState<AllAnswers>({});
  const [answersLoaded, setAnswersLoaded] = useState(false);

  const [coverage, setCoverage] = useState<Coverage | null>(null);
  const [coverageLoading, setCoverageLoading] = useState(false);

  const loadQuestions = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/framework/attestation/questions', { params: { project_id: projectId ?? undefined } });
      setQuestions(Array.isArray(res?.data?.questions) ? res.data.questions : []);
      setRequired(Array.isArray(res?.data?.required) ? res.data.required : []);
    } catch {
      setQuestions([]); setRequired([]);
    } finally {
      setQuestionsLoaded(true);
    }
  }, [projectId]);

  const loadConfig = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/framework/attestation/config');
      setConfig({
        submission_list: Array.isArray(res?.data?.submission_list) ? res.data.submission_list : [],
        system_list: Array.isArray(res?.data?.system_list) ? res.data.system_list : [],
      });
    } catch {
      setConfig(EMPTY_CONFIG);
    } finally {
      setConfigLoaded(true);
    }
  }, []);

  const saveConfig = useCallback(async (next: AttestationConfig) => {
    try {
      const res = await apiClient.put('/api/framework/attestation/config', next);
      setConfig({
        submission_list: Array.isArray(res?.data?.submission_list) ? res.data.submission_list : [],
        system_list: Array.isArray(res?.data?.system_list) ? res.data.system_list : [],
      });
      // Q5/Q10's resolved options depend on this config — refresh the schema so the form reflects
      // the edited lists immediately instead of on next reload.
      await loadQuestions();
      return { ok: true };
    } catch (e: any) {
      return { ok: false, error: e?.response?.data?.error || e?.message || 'save failed' };
    }
  }, [loadQuestions]);

  const loadAnswers = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/framework/attestation/answers');
      setAnswers(res?.data?.answers && typeof res.data.answers === 'object' ? res.data.answers : {});
    } catch {
      setAnswers({});
    } finally {
      setAnswersLoaded(true);
    }
  }, []);

  const saveAnswers = useCallback(async (eucId: string, eucAnswers: Record<string, { value: string | string[]; extra?: string | null }>) => {
    try {
      const res = await apiClient.put(`/api/framework/attestation/answers/${encodeURIComponent(eucId)}`, { answers: eucAnswers }, { params: { project_id: projectId ?? undefined } });
      await loadAnswers();
      return { ok: true, saved: res?.data?.saved, errors: res?.data?.errors || [] };
    } catch (e: any) {
      return { ok: false, error: e?.response?.data?.error || e?.message || 'save failed', errors: e?.response?.data?.errors };
    }
  }, [loadAnswers, projectId]);

  const loadCoverage = useCallback(async () => {
    setCoverageLoading(true);
    try {
      const res = await apiClient.get('/api/framework/attestation/coverage', { params: { project_id: projectId ?? undefined } });
      setCoverage(res?.data || null);
    } catch {
      setCoverage(null);
    } finally {
      setCoverageLoading(false);
    }
  }, [projectId]);

  // Per-owner xlsx pack — same download-a-blob pattern as every other export in this app
  // (ArtifactPanel.tsx's downloadText / api.ts's exportProcessMapAuditPDF).
  const exportPack = useCallback(async (owner: string) => {
    try {
      const res = await apiClient.get('/api/framework/attestation/export', { params: { owner, project_id: projectId ?? undefined }, responseType: 'blob' });
      const blob = res.data as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attestation_${owner.replace(/[^a-z0-9]+/gi, '_')}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      return { ok: true };
    } catch (e: any) {
      return { ok: false, error: e?.response?.data?.error || e?.message || 'export failed' };
    }
  }, [projectId]);

  const importFile = useCallback(async (file: File) => {
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await apiClient.post('/api/framework/attestation/import', form, { params: { project_id: projectId ?? undefined } });
      await Promise.all([loadAnswers(), loadCoverage()]);
      return { ok: true, result: res?.data as ImportResult };
    } catch (e: any) {
      return { ok: false, error: e?.response?.data?.error || e?.message || 'import failed' };
    }
  }, [loadAnswers, loadCoverage, projectId]);

  // The paste path — frontend already split a pasted TSV/CSV block into rows before calling this.
  const importRows = useCallback(async (rows: { euc_id: string; question_id: string; response: string; notes?: string }[]) => {
    try {
      const res = await apiClient.post('/api/framework/attestation/import', { rows }, { params: { project_id: projectId ?? undefined } });
      await Promise.all([loadAnswers(), loadCoverage()]);
      return { ok: true, result: res?.data as ImportResult };
    } catch (e: any) {
      return { ok: false, error: e?.response?.data?.error || e?.message || 'import failed' };
    }
  }, [loadAnswers, loadCoverage, projectId]);

  // Re-fetches whenever the current project changes — loadQuestions/loadCoverage's own dep arrays
  // include projectId, so this effect naturally re-runs on project switch, same pattern as
  // useFrameworkData.ts's reload().
  useEffect(() => { loadQuestions(); loadConfig(); loadAnswers(); loadCoverage(); }, [loadQuestions, loadConfig, loadAnswers, loadCoverage]);

  return {
    questions, required, questionsLoaded, loadQuestions,
    config, configLoaded, loadConfig, saveConfig,
    answers, answersLoaded, loadAnswers, saveAnswers,
    coverage, coverageLoading, loadCoverage,
    exportPack, importFile, importRows,
  };
}
